import { allElements } from "constants/constants";
import { LocationsEntity } from "entities/Locations/LocationsEntity";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { IFailureType, IMillFilters, IOption } from "./SummaryReportFiltersInterfaces";
import { getFirstDayOfTheMonthAtZeroHour, getTodayAtEndOfDay } from "utilities/utilities";
import { SummaryReportDashboardContext } from "contexts/SummaryReportDashboardContext/SummaryReportDashboardContext";
import { handleNameTranslation } from "utilities/localesUtilities";

const firstDayOfTheMonth = getFirstDayOfTheMonthAtZeroHour();
const today = getTodayAtEndOfDay();

export const initialMillFilters: IMillFilters = {
    startDate: firstDayOfTheMonth,
    endDate: today,
    productionLine: allElements,
    machineCenter: [allElements],
    functionalLocation: allElements,
    shift: allElements,
    displayIn: "minutes",
    failureTypes: [allElements],
};

const useMillFilters = () => {
    const { millId } = useParams();
    const { summaryReportFiltersData, loading } = LocationsEntity.GetSummaryReportFilters(Number(millId));
    const [filters, setFilters] = useState<IMillFilters>(initialMillFilters);
    const isLoading = loading || !summaryReportFiltersData?.production_line;
    const { handleFiltersChange: handleSetFilters } = useContext(SummaryReportDashboardContext);
    const { t, i18n } = useTranslation();

    const displayInOptions = useMemo(
        () => [
            { id: "occurrences", name: t("locations.summary_report.filters.occurrences") },
            { id: "minutes", name: t("locations.summary_report.filters.minutes") },
        ],
        [t],
    );

    const failureTypeOptions: IFailureType[] = useMemo(
        () => summaryReportFiltersData?.failure_type?.map((item) => item) || [],
        [summaryReportFiltersData.failure_type],
    );

    const allLabel = useMemo(() => t("locations.summary_report.filters.all"), [t]);

    const productionLinesOptions = useMemo(() => {
        if (loading) return [];
        const productionLinesList =
            summaryReportFiltersData?.production_line?.map((productionLine) => {
                return {
                    id: productionLine.production_line_id,
                    name: productionLine.name,
                } as IOption;
            }) || [];

        productionLinesList.unshift({ id: allElements, name: allLabel });
        return productionLinesList;
    }, [allLabel, loading, summaryReportFiltersData?.production_line]);

    const functionalLocationsOptions = useMemo(() => {
        if (loading) return [];
        const functionalLocationsList = [] as IOption[];

        let productionLines = summaryReportFiltersData?.production_line || [];

        if (filters.productionLine !== allElements) {
            productionLines = productionLines?.filter((productionLine) => {
                return productionLine.production_line_id === Number(filters.productionLine);
            });
        }

        productionLines.forEach((production_line) => {
            let machineCenters = production_line?.machine_centers || [];
            if (filters.machineCenter?.length! > 0 && !filters.machineCenter?.includes(allElements)) {
                machineCenters = machineCenters?.filter((machineCenter) => {
                    return machineCenter.machine_center_id === Number(filters.machineCenter);
                });
            }

            machineCenters.forEach((machine_center) => {
                let areas = machine_center.areas;
                areas.forEach((area) => {
                    let functionLocations = area.functional_locations;
                    functionLocations.forEach((functional_location) => {
                        if (functional_location.location_specific_functional_location_failure_codes.length > 0) {
                            functionalLocationsList.push({
                                id: functional_location.functional_location_id,
                                name: handleNameTranslation(
                                    i18n.language,
                                    functional_location.translation.translation_names,
                                ),
                                tag:
                                    filters.machineCenter?.length! > 0 && filters.machineCenter?.includes(allElements)
                                        ? `${handleNameTranslation(
                                              i18n.language,
                                              area.translation.translation_names,
                                          )} | ${
                                              machine_center.short_name
                                                  ? machine_center.short_name
                                                  : machine_center.short_name || machine_center.name
                                          }`
                                        : undefined,
                            });
                        } else {
                            return true;
                        }
                    });
                });
            });
        });

        functionalLocationsList.unshift({ id: allElements, name: allLabel });

        return functionalLocationsList;
    }, [allLabel, filters, loading, summaryReportFiltersData?.production_line, i18n.language]);

    const machineCentersOptions = useMemo(() => {
        if (loading) return [];
        const machineCentersList = [] as IOption[];
        let productionLines = summaryReportFiltersData?.production_line || [];

        if (filters.productionLine !== allElements) {
            productionLines = productionLines?.filter((productionLine) => {
                return productionLine.production_line_id === Number(filters.productionLine);
            });
        }

        productionLines.forEach((production_line) => {
            production_line.machine_centers.forEach((machine_center) => {
                machineCentersList.push({
                    id: machine_center.machine_center_id,
                    name: machine_center.name,
                    tag: filters.productionLine === allElements ? production_line.name : undefined,
                });

                machine_center.non_location_specific_machine_center_failure_codes
                    .filter(
                        (row) =>
                            row.failure_code.failure_type.corporate_id !== null &&
                            (filters.machineCenter?.length! > 0 && !filters.machineCenter?.includes(allElements)
                                ? filters.machineCenter?.includes(String(row.machine_center_id))
                                : row),
                    )
                    .forEach((row) => {
                        const existingFailureType = failureTypeOptions.find(
                            (failureType) =>
                                failureType.failure_type_id === row.failure_code.failure_type.failure_type_id,
                        );

                        if (!existingFailureType) {
                            failureTypeOptions.push(row.failure_code.failure_type);
                        }
                    });

                machine_center.areas.forEach((area) => {
                    area.functional_locations.forEach((functionalLocations) => {
                        if (functionalLocations.location_specific_functional_location_failure_codes.length > 0) {
                            functionalLocations.location_specific_functional_location_failure_codes
                                .filter((row) =>
                                    functionalLocationsOptions.length > 1
                                        ? filters.functionalLocation !== allElements
                                            ? row.functional_location_id === filters.functionalLocation
                                            : functionalLocationsOptions.some(
                                                  (option) => option.id === row.functional_location_id,
                                              )
                                        : null,
                                )
                                .forEach((row) => {
                                    const existingFailureType = failureTypeOptions.find(
                                        (failureType) =>
                                            failureType.failure_type_id ===
                                            row.failure_code.failure_type.failure_type_id,
                                    );
                                    if (!existingFailureType) {
                                        failureTypeOptions.push(row.failure_code.failure_type);
                                    }
                                });
                        }
                    });
                });
            });
        });

        machineCentersList.unshift({ id: allElements, name: allLabel });

        return machineCentersList;
    }, [
        allLabel,
        filters.productionLine,
        loading,
        summaryReportFiltersData?.production_line,
        failureTypeOptions,
        filters.machineCenter,
        filters.functionalLocation,
        functionalLocationsOptions,
    ]);

    const shiftsOptions = useMemo(() => {
        if (loading) return [];

        const allShiftsList: Array<IOption> = [];

        const productionLinesShifts: { shift_id: number; name: string }[] = (
            [] as { shift_id: number; name: string }[]
        ).concat(
            ...(summaryReportFiltersData?.production_line?.map((productionLine) => {
                if (filters.productionLine !== allElements) {
                    return productionLine.production_line_shifts
                        .filter((shift) => shift.production_line_id === filters.productionLine)
                        .flatMap((productionLineShift) => productionLineShift.shift);
                } else {
                    return productionLine.production_line_shifts.flatMap(
                        (productionLineShift) => productionLineShift.shift,
                    );
                }
            }) || []),
        );

        Object.values(productionLinesShifts)?.forEach((shift) => {
            const existingShift = allShiftsList.find((shiftItem) => shiftItem.id === shift.shift_id);
            if (!existingShift) {
                allShiftsList.push({
                    id: shift.shift_id,
                    name: shift.name,
                } as IOption);
            }
        });

        allShiftsList.unshift({ id: allElements, name: allLabel });

        return allShiftsList;
    }, [allLabel, loading, summaryReportFiltersData.production_line, filters.productionLine]);

    const failureTypesOptions = useMemo(() => {
        if (loading) return [];
        const failureTypesList =
            failureTypeOptions?.map((failureType) => {
                return {
                    id: failureType.failure_type_id,
                    name: failureType.name,
                } as IOption;
            }) || [];
        failureTypesList.unshift({ id: allElements, name: allLabel });
        return failureTypesList;
    }, [allLabel, loading, failureTypeOptions]);

    const handleFiltersChange = useCallback(
        (newFilters: IMillFilters) => {
            if (Array.isArray(newFilters.failureTypes)) {
                if (newFilters.failureTypes.length <= 1) {
                    newFilters.failureTypes = [allElements];
                } else if (newFilters.failureTypes.length > 0 && newFilters.failureTypes.indexOf(allElements) === 0) {
                    newFilters.failureTypes = newFilters.failureTypes.filter((failureType) => {
                        return failureType !== allElements;
                    });
                } else if (newFilters.failureTypes.length > 1 && newFilters.failureTypes.includes(allElements)) {
                    newFilters.failureTypes = newFilters.failureTypes.filter((failureType) => {
                        return failureType === allElements;
                    });
                }
            }
            if (Array.isArray(newFilters.machineCenter)) {
                if (newFilters.machineCenter.length <= 1) {
                    newFilters.machineCenter = [allElements];
                } else if (newFilters.machineCenter.length > 0 && newFilters.machineCenter.indexOf(allElements) === 0) {
                    newFilters.machineCenter = newFilters.machineCenter.filter((machineCenter) => {
                        return machineCenter !== allElements;
                    });
                } else if (newFilters.machineCenter.length > 1 && newFilters.machineCenter.includes(allElements)) {
                    newFilters.machineCenter = newFilters.machineCenter.filter((machineCenter) => {
                        return machineCenter === allElements;
                    });
                }
            }

            setFilters({ ...filters, ...newFilters });
        },
        [filters],
    );

    useEffect(() => {
        const availableShiftInProductionLine = shiftsOptions.some((option) => option.id === filters.shift);

        if (!availableShiftInProductionLine) {
            handleFiltersChange({ shift: allElements });
        }
    }, [filters, shiftsOptions, handleFiltersChange]);

    const applyFilters = () => {
        handleSetFilters(filters);
    };

    const resetFilters = () => {
        setFilters(initialMillFilters);
        handleSetFilters(initialMillFilters);
    };

    return {
        isLoading,
        productionLinesOptions,
        machineCentersOptions,
        functionalLocationsOptions,
        shiftsOptions,
        displayInOptions,
        failureTypesOptions,
        applyFilters,
        resetFilters,
        filters,
        handleFiltersChange,
    };
};

export default useMillFilters;
